import { z } from "zod";
import { EQUIPMENT, type LegacyEquipmentKey } from "./arsenal.js";
import type { ContractId } from "./contracts.js";
import type { ContractTerms } from "./contract-orders.js";
import { MODULE_IDS, WEAPON_IDS, type ModuleId } from "./definitions.js";
import {
  ADVANCED_REPUTATION,
  CUSTOMER_IDS,
  contractCustomer,
  contractReputation,
  type ContractDifficulty,
  type CustomerId,
} from "./progression.js";
import { CARGO_REWARDS, type LootCargoItem, type LootCargoKind } from "./risk.js";
import { TUTORIAL } from "./tutorial.js";

export interface ContractRewardTerms {
  reputation: { customerId: CustomerId; amount: number } | null;
  blueprints: { key: LegacyEquipmentKey; successes: number }[];
  firstModule: ModuleId | null;
}
export interface ContractAccessTerms {
  customerId: CustomerId;
  minimumReputation: number;
}
export interface CargoRewardTerms {
  contentVersion: "cargo-rewards-v1";
  credits: string;
  components: number;
}
export interface EventContributionRules {
  damage: number;
  healing: number;
  tasks: number;
}

const equipmentKeySchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("weapon"), id: z.enum(WEAPON_IDS) }),
  z.strictObject({ kind: z.literal("module"), id: z.enum(MODULE_IDS) }),
  z.strictObject({
    kind: z.literal("passive"),
    id: z.enum(["targeting", "capacitor", "repair-drone", "thrusters"]),
  }),
]);
export const contractRewardTermsSchema = z.strictObject({
  reputation: z
    .strictObject({ customerId: z.enum(CUSTOMER_IDS), amount: z.number().int().nonnegative() })
    .nullable(),
  blueprints: z
    .array(z.strictObject({ key: equipmentKeySchema, successes: z.number().int().positive() }))
    .max(100)
    .refine(
      (items) => new Set(items.map(({ key }) => `${key.kind}:${key.id}`)).size === items.length,
      { message: "Frozen blueprint rewards must be distinct" },
    ),
  firstModule: z.enum(MODULE_IDS).nullable(),
}) satisfies z.ZodType<ContractRewardTerms>;
export const contractAccessTermsSchema = z.strictObject({
  customerId: z.enum(CUSTOMER_IDS),
  minimumReputation: z.number().int().nonnegative(),
}) satisfies z.ZodType<ContractAccessTerms>;
export const cargoRewardTermsSchema = z.strictObject({
  contentVersion: z.literal("cargo-rewards-v1"),
  credits: z.string().regex(/^(0|[1-9]\d*)$/),
  components: z.number().int().nonnegative(),
}) satisfies z.ZodType<CargoRewardTerms>;
export const eventContributionRulesSchema = z.strictObject({
  damage: z.number().finite().positive(),
  healing: z.number().finite().positive(),
  tasks: z.number().finite().positive(),
}) satisfies z.ZodType<EventContributionRules>;

export const EVENT_CONTRIBUTION_RULES: EventContributionRules = {
  damage: 20,
  healing: 12,
  tasks: 1,
};

export function createContractRewardTerms(
  contractId: ContractId,
  difficulty: ContractDifficulty,
): ContractRewardTerms {
  const customerId = contractCustomer(contractId);
  return contractRewardTermsSchema.parse({
    reputation: customerId === null ? null : { customerId, amount: contractReputation(difficulty) },
    blueprints: EQUIPMENT.filter((item) => item.blueprint?.contractId === contractId).map(
      (item) => ({ key: item.key, successes: item.blueprint?.successes }),
    ),
    firstModule:
      contractId === "intro"
        ? TUTORIAL.firstModule
        : contractId === "hunt" || contractId === "salvage"
          ? "mine"
          : null,
  });
}
export function createContractAccessTerms(
  contractId: ContractId,
  difficulty: ContractDifficulty,
): ContractAccessTerms | null {
  const customerId = contractCustomer(contractId);
  return difficulty === "advanced" && customerId !== null
    ? contractAccessTermsSchema.parse({ customerId, minimumReputation: ADVANCED_REPUTATION })
    : null;
}

// These literals describe the released v1 policy, not today's definitions.
function releasedCustomer(contractId: ContractId): CustomerId | null {
  return contractId === "hunt"
    ? "security"
    : contractId === "salvage"
      ? "research"
      : contractId === "signal" || contractId === "escort"
        ? "logistics"
        : null;
}
function releasedBlueprints(contractId: ContractId): LegacyEquipmentKey[] {
  switch (contractId) {
    case "hunt":
      return [
        { kind: "weapon", id: "laser" },
        { kind: "weapon", id: "flak" },
        { kind: "weapon", id: "rail" },
        { kind: "module", id: "mine" },
        { kind: "module", id: "jammer" },
        { kind: "module", id: "interceptor" },
        { kind: "passive", id: "targeting" },
      ];
    case "salvage":
      return [
        { kind: "weapon", id: "missile" },
        { kind: "weapon", id: "ion" },
        { kind: "module", id: "repair-field" },
        { kind: "module", id: "scan" },
        { kind: "passive", id: "capacitor" },
      ];
    case "signal":
      return [
        { kind: "passive", id: "repair-drone" },
        { kind: "passive", id: "thrusters" },
      ];
    case "intro":
    case "escort":
      return [];
  }
}
export function contractRewardTerms(
  terms: ContractTerms,
  difficulty: ContractDifficulty,
): ContractRewardTerms {
  if (terms.contentVersion !== "contract-orders-v1")
    return contractRewardTermsSchema.parse(terms.rewards);
  const customerId = releasedCustomer(terms.id);
  return {
    reputation:
      customerId === null ? null : { customerId, amount: difficulty === "advanced" ? 15 : 10 },
    blueprints: releasedBlueprints(terms.id).map((key) => ({ key, successes: 1 })),
    firstModule:
      terms.id === "intro"
        ? "volley"
        : terms.id === "hunt" || terms.id === "salvage"
          ? "mine"
          : null,
  };
}
export function contractAccessTerms(
  terms: ContractTerms,
  difficulty: ContractDifficulty,
): ContractAccessTerms | null {
  if (terms.contentVersion !== "contract-orders-v1")
    return terms.access === null ? null : contractAccessTermsSchema.parse(terms.access);
  const customerId = releasedCustomer(terms.id);
  return difficulty === "advanced" && customerId !== null
    ? { customerId, minimumReputation: 20 }
    : null;
}

export function snapshotCargoReward(kind: LootCargoKind): CargoRewardTerms {
  return cargoRewardTermsSchema.parse({
    contentVersion: "cargo-rewards-v1",
    ...CARGO_REWARDS[kind],
  });
}
export function cargoRewardTerms(item: Pick<LootCargoItem, "kind" | "rewardTerms">): CargoRewardTerms {
  if (item.rewardTerms !== undefined) return cargoRewardTermsSchema.parse(item.rewardTerms);
  const released: Record<LootCargoKind, { credits: string; components: number }> = {
    energy: { credits: "60", components: 1 },
    salvage: { credits: "100", components: 3 },
    contraband: { credits: "300", components: 6 },
  };
  return { contentVersion: "cargo-rewards-v1", ...released[item.kind] };
}
export function eventContributionRules(terms: {
  version: "public-events-v2" | "public-events-v3" | "public-events-v4";
  contributionRules?: EventContributionRules;
}): EventContributionRules {
  if (terms.version === "public-events-v4") throw new Error("Truce hunts use positive damage or defender healing");
  if (terms.version === "public-events-v2") return { damage: 20, healing: 12, tasks: 1 };
  if (terms.contributionRules === undefined)
    throw new Error("Relay capture has its own participation rules");
  return eventContributionRulesSchema.parse(terms.contributionRules);
}
